'use client'

import Link from 'next/link'
import Image from 'next/image'

import { useState, useEffect, useCallback, Suspense } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight } from '@awesome.me/kit-94e593bf23/icons/sharp/light'

import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import AutoScroll from 'embla-carousel-auto-scroll'

import '@/app/_styles/featured_items.css'

const SlideShowNav = ({ embla }) => {
    
    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
    const [nextBtnDisabled, setNextBtnDisabled] = useState(true)
    const [numSlides, setNumSlides] = useState(1)
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
    const [slidesInView, setSlidesInView] = useState(1)
    const [numSlidesInView, setNumSlidesInView] = useState(1)
    
    const onSelect = useCallback((embla) => {
        setPrevBtnDisabled( ! embla.canScrollPrev())
        setNextBtnDisabled( ! embla.canScrollNext())
    }, [])

    const updateCounters = useCallback(() => {
        if ( ! embla) return
        const emblaSlides = embla.slideNodes()
        setNumSlides(emblaSlides.length)
        const siv = embla.slidesInView(true);
        setSlidesInView(siv)
        setNumSlidesInView(siv.length)
        setCurrentSlideIndex(parseInt(embla.selectedScrollSnap()) + 1)
        //console.log('updateCounters')
    }, [embla])

    useEffect(() => {
        if ( ! embla) return
        onSelect(embla)
        embla.on('reInit', onSelect).on('select', onSelect).on('slidesInView', updateCounters)
        updateCounters()
    }, [embla, onSelect, updateCounters])

    const onPrevButtonClick = useCallback(() => {
        if ( ! embla) return
        embla.scrollPrev()
        updateCounters()
    }, [embla, updateCounters])

    const onNextButtonClick = useCallback(() => {
        if ( ! embla) return
        embla.scrollNext()
        updateCounters()
    }, [embla, updateCounters])
    
    const firstVisible = slidesInView.length > 0 ? Math.min(...slidesInView) + 1 : 1
    const lastVisible = slidesInView.length > 0 ? Math.max(...slidesInView) + 1 : 1

    return (
        <div className="flex justify-end items-center gap-2 mt-1">
            <button type="button" onClick={onPrevButtonClick} disabled={prevBtnDisabled} aria-label="Previous" className={`flex items-center justify-center px-4 py-1.5 bg-gray-3 ${prevBtnDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}><FontAwesomeIcon icon={faChevronLeft} className={`text-sm ${prevBtnDisabled ? 'text-light-4' : 'text-dark'}`} /></button>
            <button type="button" onClick={onNextButtonClick} disabled={nextBtnDisabled} aria-label="Next" className={`flex items-center justify-center px-4 py-1.5 bg-gray-3 ${nextBtnDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}><FontAwesomeIcon icon={faChevronRight} className={`text-sm ${nextBtnDisabled ? 'text-light-4' : 'text-dark'}`} /></button>
        </div>
    )
}

const FeaturedProductImage = ({ featured_product_images, isLarge = false }) => {

    const [featuredImage, setFeaturedImage] = useState(false)

    const randomize = useCallback((items) => {
        const randomIndex = Math.floor(Math.random() * items.length);
        const item = items[randomIndex];
            if (item) {
                setFeaturedImage(item)
            }
    }, [])

    useEffect(() => {
        if (featured_product_images && featured_product_images.length > 0) {
            randomize(featured_product_images)
        } else {
            // Set default image when no featured images available
            /*setFeaturedImage({
                file_name: 'https://cdn.dev.hivemodern.com/resources/neat-dining-chair-blu-dot-thumb-17e62e292d.jpg',
                alt_text: 'Featured product',
                width: 150,
                height: 150,
                isFullUrl: true
            })*/
        }
    }, [featured_product_images, randomize])

    const sizeClasses = isLarge
        ? "w-[110px] h-[110px] sm:w-[120px] sm:h-[120px] md:w-[180px] md:h-[180px] md:max-h-[180px] object-contain"
        : "w-[110px] h-[110px] sm:w-[120px] sm:h-[120px] md:w-[160px] md:h-[160px] object-contain"

    const imageSizes = isLarge
        ? "(max-width: 640px) 180px, (max-width: 768px) 200px, 240px"
        : "(max-width: 640px) 110px, (max-width: 768px) 120px, 160px"

    return (
        <>
            {featuredImage &&
                <Image
                    className={sizeClasses}
                    src={featuredImage.isFullUrl ? featuredImage.file_name : `${process.env.NEXT_PUBLIC_RESOURCE_URL}/${featuredImage.file_name}`}
                    alt={featuredImage.alt_text+' alt text'}
                    width={isLarge ? 240 : 160}
                    height={isLarge ? 240 : 160}
                    quality={90}
                    sizes={imageSizes}
                />
            }
        </>
    )
}

// Shuffle only the sale cards (they carry a discount_id), leaving featured
// items pinned at the slots the owner positioned them in
const shuffleSaleItems = (items) => {
    const saleSlots = []
    const sales = []
    items.forEach((item, index) => {
        if (item.discount_id) {
            saleSlots.push(index)
            sales.push(item)
        }
    })
    for (let i = sales.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[sales[i], sales[j]] = [sales[j], sales[i]]
    }
    const result = [...items]
    saleSlots.forEach((slot, k) => {
        result[slot] = sales[k]
    })
    return result
}

const FeaturedItems = ({ items = [], randomize = false, section_title = '', section_url = '/', section_url_title = '' }) => {

    // Server render and hydration must agree, so both use the given order.
    // When randomize is on, the carousel stays hidden until the client-side
    // shuffle picks the real order, then fades in - the shuffle has to happen
    // on the client because the home page cache would freeze a server-side
    // one into the cache entry, showing every visitor the same "random" order
    const [displayItems, setDisplayItems] = useState(items)
    const [revealed, setRevealed] = useState( ! randomize)

    useEffect(() => {
        if (randomize) {
            setDisplayItems(shuffleSaleItems(items))
            setRevealed(true)
        } else {
            setDisplayItems(items)
        }
    }, [items, randomize])

    const [emplaOptions, setEmplaOptions] = useState({
        loop: false,
        duration: 45,
        slidesToScroll: 1,
        align: 'start',
    })
    
    /*const [autoplayOptions, setAutoplayOptions] = useState({
        delay: 4000,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
    })*/
    
    /*const [autoScrollOptions, setAutoScrollOptions] = useState({
        startDelay:1000,
        playOnInit:true,
        stopOnMouseEnter:true,
    })*/
    
    const [viewportRef, embla] = useEmblaCarousel(emplaOptions, []);
    //const [viewportRef, embla] = useEmblaCarousel(emplaOptions, [Autoplay(autoplayOptions)]);
    //const [viewportRef, embla] = useEmblaCarousel(emplaOptions, [Autoplay(autoplayOptions), AutoScroll(autoScrollOptions)]);
    
    // NOTE todo.  if there are less than three slides we need to add a class to make:
    // a single slide 100% width
    // two slides 50/50
    
    return (
        <>
            <div className="flex items-end mb-4 pb-1 w-full overflow-hidden site-padding min-[1810px]:px-0">
                {section_title && (
                    section_url ? (
                        <Link href={section_url}><h2 className="block text-4xl font-extralight me-3 leading-none hover:text-primary transition-colors">{section_title}</h2></Link>
                    ) : (
                        <h2 className="block text-4xl font-extralight me-3 leading-none">{section_title}</h2>
                    )
                )}
                {section_url && section_url_title && (
                    <Link href={section_url} className={`text-3xs text-light underline transition-colors hover:text-primary ${items.length <= 2 ? 'hidden' : ''} ${items.length > 2 ? 'hidden md:block' : ''}`}>{section_url_title}</Link>
                )}
            </div>
            <div className={`embla featured_items max-w-screen mx-auto site-padding min-[1810px]:px-0 transition-opacity duration-500 ${revealed ? 'visible opacity-100' : 'invisible opacity-0'}`}>
                <div className="embla__viewport featured_items_viewport overflow-hidden" ref={viewportRef}>
                    <div className={`embla__container featured_items_container flex backface-hidden`}>
                        {displayItems && displayItems.map((item, index) => (
                            <Link href={item.url} key={index} className={`embla__slide featured_items_slide min-w-0 ${items.length == 1 ? 'featured_items_slide_single' : ''} ${items.length == 2 ? 'featured_items_slide_double' : ''}`}>
                                <div className="max-w-full w-full relative">
                                    <div className={`absolute p-3 font-medium text-sm top-0 left-0 z-50 ${item.tag_color_class ? item.tag_color_class : 'text-primary'}`}>{ item.tag_title }</div>
                                    <div className={`flex ${items.length <= 2 ? 'flex-col md:flex-row' : 'flex-col md:flex-row'} ${items.length <= 2 ? 'justify-center md:justify-between lg:justify-center' : 'justify-center md:justify-between'} overlay-block h-[225px] px-4 py-6`}>

                                        <div className={`flex flex-col justify-center md:justify-end items-center md:items-start mb-2 md:mb-0`}>
                                            <FeaturedProductImage featured_product_images={item.featured_product_images} isLarge={items.length === 1} />
                                        </div>

                                        <div className={`${items.length <= 2 ? 'flex md:block flex-col justify-end text-right md:absolute md:right-6 md:bottom-6' : 'flex flex-col justify-end text-right'}`}>
                                            {item.brand_logo && item.brand_logo.file_name &&
                                                <Image
                                                    className="max-h-full w-auto ml-auto mb-4"
                                                    src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${item.brand_logo.file_name}`}
                                                    alt={item.brand_logo.alt_text+' alt text'}
                                                    width={item.brand_logo.width}
                                                    height={item.brand_logo.height}
                                                    quality={90}
                                                    sizes="(max-width: 768px) 150px, 200px"
                                                />
                                            }
                                            <div className="text-right">
                                                {(() => {
                                                    const lines = [
                                                        { content: item.banner_line_1, isHtml: true },
                                                        { content: item.banner_line_2, isHtml: false },
                                                        { content: item.banner_line_3, isHtml: false },
                                                        { content: item.banner_line_4, isHtml: false },
                                                    ].filter(line => line.content)

                                                    return lines.map((line, idx) => (
                                                        line.isHtml ? (
                                                            <div
                                                                key={idx}
                                                                className={`text-2xs md:text-sm capitalize ${idx === 0 ? 'font-medium' : ''}`}
                                                                dangerouslySetInnerHTML={{ __html: line.content }}
                                                            />
                                                        ) : (
                                                            <div
                                                                key={idx}
                                                                className={`text-2xs md:text-sm capitalize ${idx === 0 ? 'font-medium' : ''}`}
                                                            >
                                                                {line.content}
                                                            </div>
                                                        )
                                                    ))
                                                })()}
                                            </div>
                                        </div>
                                        
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
            
            <div className={`embla__controls mt-2 site-padding min-[1810px]:px-0 ${items.length <= 2 ? 'hidden' : ''} ${items.length == 3 ? 'lg:hidden' : ''}`}>
                <SlideShowNav embla={embla} />
            </div>
            
        </>
    )
}

export default FeaturedItems
